import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './MapComponent.css';
import { FormControl, Typography, Tooltip, Paper, Autocomplete, TextField } from '@mui/material';
import { getKeyData, getSelectedGeoLocationData, getSelectedStateKeyData } from 'utility/helper';

interface DataPoint {
  latitude: number;
  longitude: number;
  vins: string[];
}

interface DataFilterMapProps {
  data: DataPoint[];
}

const customIcon = L.divIcon({
  className: 'custom-icon',
  html: '<div class="circle-icon"></div>',
  iconSize: [12, 12],
  iconAnchor: [6, 6],
  popupAnchor: [0, -6],
});

const DataFilterMap: React.FC<DataFilterMapProps> = ({ data }) => {

  const [initialPosition, setInitialPoition] = useState<[number, number]>([37.8, -96]);
  const [city, setCity] = useState<string | null>('');
  const [state, setState] = useState<string | null>('');
  const [county, setCounty] = useState<string | null>('');
  const [zipcode, setZipcode] = useState<string | null>('');
  const [cityList, setCityList] = useState<string[]>([]);
  const [stateProvinceList, setStateProvinceList] = useState<string[]>([]);
  const [zipcodeList, setZipcodeList] = useState<string[]>([]);
  const [countyList, setCountyList] = useState<string[]>([]);
  const [mapPoints, setMapPoints] = useState<any[]>(data);

  useEffect(() => {
    if (city || state || zipcode || county) {
      const updatedData = getSelectedGeoLocationData(data, [{ city_name: city }, { state_name: state }, { zip_code: zipcode }, { county_name: county }]);
      setMapPoints(updatedData);
      setInitialPoition([updatedData[0]?.latitude, updatedData[0]?.longitude]);
    }
    if (state) {
      const selectionData = [state,city,county,zipcode];
      const { cityList, zipcodeList, countyList } = getSelectedStateKeyData(data, selectionData)
      setCityList(cityList);
      setZipcodeList(zipcodeList);
      setCountyList(countyList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city, state, zipcode, county])

  useEffect(() => {
    const { cityList, stateList, zipcodeList, countyList } = getKeyData(data);
    setCityList(cityList);
    setStateProvinceList(stateList);
    setZipcodeList(zipcodeList);
    setCountyList(countyList);
  }, [data])

  function LocationMarker(props: any) {
    const { mapPoints } = props;
    const data = { lat: props.mapPoints[0].latitude, lng: props.mapPoints[0].longitude }
    const map = useMapEvents({})

    useEffect(() => {
      if((city || state || zipcode || county)) {
        map.flyTo(data, 12)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapPoints])

    return null;
  }
  const USA_BOUNDS: any = [
    [24.396308, -125.0],  // South-West coordinates (latitude, longitude)
    [49.384358, -66.93457] // North-East coordinates (latitude, longitude)
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
      <MapContainer
        center={initialPosition}
        zoom={(city || state || zipcode || county) ? 9 : 4}
        style={{ height: "80vh", width: "70%" }}
        zoomControl={false}
        bounds={USA_BOUNDS}
        boundsOptions={{ padding: [20, 20] }}
      >
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
        />
        {mapPoints?.length > 0 && mapPoints?.map((point, index) => (
          <Marker
            key={index}
            position={[point.latitude, point.longitude]}
            icon={customIcon}
          >
            <Popup>
              <div>
                <strong>VINS:</strong>
                <ul>
                  {point?.vins?.map((vin: string, vinIndex: number) => (
                    <li key={vinIndex}>{vin}</li>
                  ))}
                </ul>
              </div>
            </Popup>
          </Marker>
        ))}
        <LocationMarker mapPoints={mapPoints} />
      </MapContainer>


      <Paper
        elevation={2}
        sx={{
          margin: "0 10px",
          padding: '2%',
          width: '50%'
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Typography
            fontWeight="bold"
            color="black"
            style={{ paddingRight: "16px", fontSize: '16px', paddingBottom: '8px' }}
          >
            Location Filters
          </Typography>
          <div className="leftPanel" style={{ width: "100%" }}>
            <Typography
              fontWeight="bold"
              color="black"
              style={{ paddingRight: "16px", fontSize: '16px', paddingBottom: '8px' }}
            >
              State/Province </Typography>
            <div className="vinFilter" style={{ display: 'flex', flexDirection: 'column' }}>

              <Tooltip title={state?.toString()} placement="right">
                <FormControl style={{ width: "90%", paddingBottom: '8px' }} required>
                  <Autocomplete
                    onChange={(event: any, newValue: string | null) => {
                      setState(newValue);
                    }}
                    id="controllable-states-demo"
                    options={stateProvinceList}
                    sx={{ width: 300 }}
                    renderInput={(params) => <TextField {...params} label="Select / Search State/Province" />}
                    disablePortal
                  />
                </FormControl>
              </Tooltip>
            </div>
          </div>
          <div className="leftPanel" style={{ width: "100%" }}>
            <Typography
              fontWeight="bold"
              color="black"
              style={{ paddingRight: "16px", fontSize: '16px', paddingBottom: '8px' }}
            >
              City
            </Typography>
            <div className="vinFilter" style={{ display: 'flex', flexDirection: 'column' }}>
              <Tooltip title={city?.toString()} placement="right">
                <FormControl style={{ width: "90%", paddingBottom: '8px' }} required>
                  <Autocomplete
                    onChange={(event: any, newValue: string | null) => {
                      setCity(newValue?.split(',')[0] as string);
                    }}
                    id="controllable-states-demo"
                    options={cityList}
                    sx={{ width: 300 }}
                    renderInput={(params) => <TextField {...params} label="Select / Search City" />}
                  />
                </FormControl>
              </Tooltip>
            </div>
          </div>

          <div className="leftPanel" style={{ width: "100%" }}>
            <Typography
              fontWeight="bold"
              color="black"
              style={{ paddingRight: "16px", fontSize: '16px', paddingBottom: '8px' }}
            >
              County </Typography>
            <div className="vinFilter" style={{ display: 'flex', flexDirection: 'column' }}>

              <Tooltip title={zipcode?.toString()} placement="right">
                <FormControl style={{ width: "90%", paddingBottom: '8px' }} required>
                  <Autocomplete
                    onChange={(event: any, newValue: string | null) => {
                      setCounty(newValue);
                    }}
                    id="controllable-states-demo"
                    options={countyList}
                    sx={{ width: 300 }}
                    renderInput={(params) => <TextField {...params} label="Select / Search County" />}
                  />
                </FormControl>
              </Tooltip>
            </div>
          </div>
          <div className="leftPanel" style={{ width: "100%" }}>
            <Typography
              fontWeight="bold"
              color="black"
              style={{ paddingRight: "16px", fontSize: '16px', paddingBottom: '8px' }}
            >
              Zipcode </Typography>
            <div className="vinFilter" style={{ display: 'flex', flexDirection: 'column' }}>

              <Tooltip title={zipcode?.toString()} placement="right">
                <FormControl style={{ width: "90%", paddingBottom: '8px' }} required>
                  <Autocomplete
                    onChange={(event: any, newValue: string | null) => {
                      setZipcode(newValue);
                    }}
                    id="controllable-states-demo"
                    options={zipcodeList}
                    sx={{ width: 300 }}
                    renderInput={(params) => <TextField {...params} label="Select / Search Zipcode" />}
                  />
                </FormControl>
              </Tooltip>
            </div>
          </div>
        </div>
      </Paper>
    </div>
  );
};

export default DataFilterMap;
